import { useState } from "react"
import { PencilIcon, TrashIcon, PlusIcon } from "@heroicons/react/24/outline"
import type { Adset, CampaignData } from "../types"

interface AdsetReviewProps {
  campaignTitle: string
  adsets: Adset[]
  campaignData: CampaignData
  onAdsetsChange: (adsets: Adset[]) => void
  onNext: () => void
  onBack: () => void
}

export default function AdsetReview({
  campaignTitle,
  adsets,
  campaignData,
  onAdsetsChange,
  onNext,
  onBack,
}: AdsetReviewProps) {
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editValue, setEditValue] = useState("")

  const handleStartEdit = (adset: Adset) => {
    setEditingId(adset.id)
    setEditValue(adset.title)
  }

  const handleSaveEdit = () => {
    if (!editingId || !editValue.trim()) return
    onAdsetsChange(
      adsets.map(a =>
        a.id === editingId ? { ...a, title: editValue.trim() } : a,
      ),
    )
    setEditingId(null)
    setEditValue("")
  }

  const handleCancelEdit = () => {
    setEditingId(null)
    setEditValue("")
  }

  const handleDelete = (id: string) => {
    if (adsets.length <= 1) return
    onAdsetsChange(adsets.filter(a => a.id !== id))
  }

  const handleAdd = () => {
    const newAdset: Adset = {
      id: crypto.randomUUID(),
      title: `${campaignData.adsetStrategy || "Adset"} - ${adsets.length + 1}`,
    }
    onAdsetsChange([...adsets, newAdset])
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-basic-white mb-1">
          Adset Review
        </h2>
        <p className="text-sm text-basic-light/60">
          Review and manage your campaign adsets
        </p>
      </div>

      <div className="bg-system-gray-900/50 border border-system-gray-700/50 rounded-lg p-4">
        <p className="text-xs text-basic-light/50 mb-1">Campaign Title</p>
        <p
          className="text-sm text-basic-white font-mono truncate"
          title={campaignTitle}
        >
          {campaignTitle}
        </p>
      </div>

      <div className="space-y-3">
        <p className="text-sm font-medium text-basic-light/70">
          Adsets ({adsets.length})
        </p>

        {adsets.map(adset => (
          <div
            key={adset.id}
            className="bg-system-gray-900/50 border border-system-gray-700/50 rounded-lg p-4"
          >
            {editingId === adset.id ? (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={editValue}
                  onChange={e => setEditValue(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === "Enter") handleSaveEdit()
                    if (e.key === "Escape") handleCancelEdit()
                  }}
                  className="input input-bordered input-sm flex-1 bg-system-gray-900/50 border-system-gray-700/50 text-basic-white"
                  autoFocus
                />
                <button
                  className="btn btn-primary btn-sm btn-ghost"
                  onClick={handleSaveEdit}
                >
                  Save
                </button>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={handleCancelEdit}
                >
                  Cancel
                </button>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm text-basic-white truncate flex-1">
                    {adset.title}
                  </span>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      className="btn btn-ghost btn-sm btn-square"
                      onClick={() => handleStartEdit(adset)}
                      title="Edit adset title"
                    >
                      <PencilIcon className="w-4 h-4" />
                    </button>
                    <button
                      className="btn btn-ghost btn-sm btn-square text-error"
                      onClick={() => handleDelete(adset.id)}
                      disabled={adsets.length <= 1}
                      title={
                        adsets.length <= 1
                          ? "At least one adset is required"
                          : "Delete adset"
                      }
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                {adset.description && (
                  <p className="text-xs text-basic-light/50 mt-1.5">
                    {adset.description}
                  </p>
                )}
              </>
            )}
          </div>
        ))}

        <button
          className="w-full border-2 border-dashed border-system-gray-700/50 rounded-lg p-4 flex items-center justify-center gap-2 text-sm text-basic-light/50 hover:text-basic-light/80 hover:border-system-gray-700 transition-colors"
          onClick={handleAdd}
        >
          <PlusIcon className="w-4 h-4" />
          Add Another Adset
        </button>
      </div>

      <div className="flex justify-between pt-4">
        <button className="btn btn-ghost" onClick={onBack}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            className="w-4 h-4"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18"
            />
          </svg>
          Back
        </button>
        <button className="btn btn-primary" onClick={onNext}>
          Next
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            className="w-4 h-4"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3"
            />
          </svg>
        </button>
      </div>
    </div>
  )
}
